import { useState, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { CULLING_DELIVERABLE_EXTS, extOf } from '@/lib/culling';
import { Download, Lock, Loader2, CheckCircle, Package, Upload, Trash2, Clock, Check } from 'lucide-react';
import { awaitingRelease } from '@/lib/draftGate';
import { EMPTY_DELIVERY, deliveryComplete, deliveryPayload } from '@/lib/editorTime';
import { DeliveryQuestions } from './DeliveryQuestions';
import { MustHavesPanel, AttestationModal, useFirstDraftChecklist } from './MustHaves';

// Delivery panel for Culling Only jobs — ONE component, two audiences:
//   client            -> files listed, locked until they Confirm & Download
//   admin/staff/editor -> files listed, upload + remove, delivery state at a glance
//
// A culling job has no review video: the deliverable IS the NLE project file, so
// there is nothing to watch, no timestamped notes, no version comparison and no
// footage analysis. This panel replaces the video review UI on culling projects.
export const CullingDelivery = ({ project, onRefresh }) => {
  const { user } = useAuth();
  const isUploader = ['admin', 'staff', 'editor'].includes(user?.role);
  const isAdmin = ['admin', 'staff'].includes(user?.role);
  const isClient = user?.role === 'client';
  const [confirming, setConfirming] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [releasing, setReleasing] = useState(false);
  // An editor's two answers about this delivery (DeliveryQuestions; the server refuses the delivery without them).
  const [delivery, setDelivery] = useState(EMPTY_DELIVERY);
  const asksDelivery = user?.role === 'editor';
  // The first-draft checklist (culling carries the notes confirmation alone): the server refuses without it.
  const checklist = useFirstDraftChecklist(project.id, asksDelivery);
  const [attesting, setAttesting] = useState(false);
  const inputRef = useRef(null);

  if (!isUploader && !isClient) return null;

  // Delivered files live on versions[].files (type project_file). For clients
  // get_project already filters versions to released-only, so nothing shows here
  // until the PM has released the delivery.
  const files = [];
  const seen = new Set();
  for (const v of (project.versions || [])) {
    for (const f of (v.files || [])) {
      if (f.type === 'project_file' && f.file_id && !seen.has(f.file_id)) {
        seen.add(f.file_id);
        files.push(f);
      }
    }
  }

  const locked = !project.client_approved;
  const confirmedOn = project.client_approved_at
    ? new Date(project.client_approved_at).toLocaleDateString()
    : '';
  const released = (project.versions || []).some((v) => v.released_to_client === true);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await api.put(`/projects/${project.id}/client-approve`);
      toast.success('Confirmed. Your download is unlocked.');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  // Admin release. Deliberately the SAME PUT /projects/{id}/approve-draft the
  // video and photo panels call — culling was the only delivery surface with no
  // control bound to it, not a product needing its own release mechanism. A
  // second endpoint here would be a second place to keep the per-version gate,
  // the client email and the status sync in agreement.
  const handleRelease = async () => {
    setReleasing(true);
    try {
      await api.put(`/projects/${project.id}/approve-draft`);
      toast.success('Released — the client can now see the file');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not release. Please try again.');
    } finally {
      setReleasing(false);
    }
  };

  const handleDownload = async (f) => {
    try {
      const r = await api.post('/storage/presign-download', { key: f.key });
      if (r.data.download_url) window.location.href = r.data.download_url;
      else toast.error('Download unavailable');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download unavailable');
    }
  };

  // Uploader delivery: presign -> PUT -> confirm-upload for each file, then ONE
  // versions/upload carrying them all as project_file. That is the Phase 2 path,
  // so the substituted guard (>=1 project_file), the editor payment and the
  // status advance all fire exactly as they do for an editor upload.
  const handleFiles = async (fileList) => {
    // Before any upload: the server refuses the delivery without the checklist or the two answers, and the files
    // would be lost.
    if (checklist.needed || checklist.pending) {
      toast.error('Confirm the checklist before delivering'); return;
    }
    if (asksDelivery && !deliveryComplete(delivery)) {
      toast.error('Answer the two questions about this delivery first'); return;
    }
    const picked = Array.from(fileList);
    const bad = picked.filter((f) => !CULLING_DELIVERABLE_EXTS.includes(extOf(f.name)));
    if (bad.length) {
      toast.error(`Unsupported file type: ${bad.map((f) => f.name).join(', ')}. `
        + `Allowed: ${CULLING_DELIVERABLE_EXTS.join(' ')}`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of picked) {
        const presign = await api.post('/storage/presign-upload', {
          filename: f.name, scope: 'project_deliverable', project_id: project.id,
          version: String((project.current_version || 0) + 1),
          content_type: f.type || 'application/octet-stream',
        });
        if (presign.data.upload_url && presign.data.upload_url !== 'LOCAL') {
          // fetch() never throws on 4xx/5xx — check res.ok so a failed PUT is not
          // confirmed as an empty "completed" deliverable.
          const putRes = await fetch(presign.data.upload_url, {
            method: 'PUT', body: f,
            headers: { 'Content-Type': f.type || 'application/octet-stream' },
          });
          if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
        } else {
          const form = new FormData(); form.append('file', f);
          await api.post(`/storage/upload/${presign.data.file_id}`, form,
            { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        await api.post(`/storage/confirm-upload/${presign.data.file_id}`);
        uploaded.push({ file_id: presign.data.file_id, key: presign.data.key,
                        name: f.name, filename: f.name, type: 'project_file' });
      }
      await api.post(`/projects/${project.id}/versions/upload`, {
        files: uploaded, review_video_key: '', review_video_file_id: '',
        status_label: 'Culling Delivered',
        ...(asksDelivery ? { delivery_answers: deliveryPayload(delivery) } : {}),
      });
      toast.success(`Delivered ${uploaded.length} file${uploaded.length > 1 ? 's' : ''}`);
      setDelivery(EMPTY_DELIVERY);
      onRefresh && onRefresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || e.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (fid) => {
    try {
      await api.delete(`/projects/${project.id}/photo-deliverables/${fid}`);
      toast.success('Removed');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    }
  };

  // ── CLIENT ──────────────────────────────────────────────────────────────
  if (isClient) {
    if (files.length === 0) {
      return (
        <div className="p-6 text-center" data-testid="culling-delivery-empty">
          <Package className="w-8 h-8 text-ug-gold/25 mx-auto mb-2" strokeWidth={1.5} />
          <p className="font-sans text-[11px] text-slate-400">Your culled project file is not ready yet.</p>
          <p className="font-sans text-[10px] text-slate-600 mt-1">
            We will email you as soon as your editor delivers it.
          </p>
        </div>
      );
    }
    return (
      <div className="p-5 space-y-3" data-testid="culling-delivery-section">
        <div>
          <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Your Delivery</p>
          <p className="font-sans text-[10px] text-slate-500 mt-0.5">
            Your culled project file, ready to open in your editing program.
          </p>
        </div>

        <div className="space-y-1.5">
          {files.map((f) => (
            <div key={f.file_id}
              className="flex items-center justify-between p-2.5 rounded-md bg-ug-surface/50 border border-ug-border-subtle"
              data-testid="culling-file-row">
              <span className="font-mono text-[10px] text-zinc-300 truncate mr-2">{f.filename || f.name}</span>
              {locked ? (
                <Lock className="w-3.5 h-3.5 text-slate-600 shrink-0" data-testid="culling-file-locked" />
              ) : (
                <button onClick={() => handleDownload(f)} data-testid="culling-download-btn"
                  className="tap-auto flex items-center gap-1.5 h-7 px-3 rounded-md border border-ug-gold/40 bg-ug-gold/15 text-[10px] font-mono font-semibold text-ug-gold hover:bg-ug-gold/25 transition-colors shrink-0">
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
              )}
            </div>
          ))}
        </div>

        {locked ? (
          <div className="pt-1 space-y-2.5">
            <p className="font-sans text-[10px] text-slate-400 leading-relaxed">
              There is nothing to review here. The culled project file is your deliverable, so one click
              confirms you have received it and unlocks the download.
            </p>
            <button onClick={handleConfirm} disabled={confirming} data-testid="culling-confirm-download-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-gold-leaf text-ug-obsidian font-sans text-[11px] font-semibold hover:brightness-110 transition-all disabled:opacity-60">
              {confirming
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Confirming...</>
                : <><Download className="w-3.5 h-3.5" /> Confirm &amp; Download</>}
            </button>
          </div>
        ) : (
          <p className="flex items-center gap-1.5 font-sans text-[10px] text-oxidized-teal pt-1"
            data-testid="culling-confirmed-note">
            <CheckCircle className="w-3.5 h-3.5" />
            {confirmedOn ? `Confirmed ${confirmedOn}. Download anytime.` : 'Confirmed. Download anytime.'}
          </p>
        )}
      </div>
    );
  }

  // ── ADMIN / STAFF / EDITOR ──────────────────────────────────────────────
  // "Delivered" is BANNED from this map. It used to carry two meanings at once —
  // the editor handed the file to us, and the client can see it — and the panel
  // read as a contradiction against the board's "Pending Review" badge: the file
  // said Delivered while the client still had nothing. Each state now names WHO
  // has the file, so no line can be read as the other event.
  const state = files.length === 0
    ? { icon: Package, tone: 'text-slate-500', text: 'Nothing submitted yet. Upload the culled project file below.' }
    : project.client_approved
    ? { icon: CheckCircle, tone: 'text-oxidized-teal', text: `Client confirmed${confirmedOn ? ` ${confirmedOn}` : ''}. Downloads are unlocked.` }
    : released
    ? { icon: Clock, tone: 'text-ug-gold/80', text: 'Released to the client. Waiting for them to confirm receipt.' }
    : { icon: Clock, tone: 'text-ug-gold/80', text: 'Submitted by the editor — not visible to the client yet. An admin checks the file, then releases it.' };

  return (
    <div className="p-5 space-y-3" data-testid="culling-delivery-admin">
      <div>
        <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Culling Delivery</p>
        <p className="font-sans text-[10px] text-slate-500 mt-0.5">
          This job delivers an NLE project file, not a review video. See the Culling tab for the full spec.
        </p>
      </div>

      <p className={`flex items-center gap-1.5 font-sans text-[10px] ${state.tone}`} data-testid="culling-delivery-state">
        <state.icon className="w-3.5 h-3.5 shrink-0" /> {state.text}
      </p>

      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((f) => (
            <div key={f.file_id}
              className="flex items-center justify-between p-2.5 rounded-md bg-ug-surface/50 border border-ug-border-subtle"
              data-testid="culling-file-row">
              <span className="font-mono text-[10px] text-zinc-300 truncate mr-2">{f.filename || f.name}</span>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => handleDownload(f)} data-testid="culling-download-btn"
                  className="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:text-ug-gold hover:bg-white/5 transition-colors" title="Download">
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(f.file_id)} data-testid="culling-delete-btn"
                  className="flex items-center justify-center w-6 h-6 rounded text-velvet-burgundy hover:bg-velvet-burgundy/10 transition-colors" title="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Admin release — SAME gate as PhotoReview.js / VideoReview.js (draft not
          yet released, editor has submitted, admin track at or past First Draft
          Complete) plus a files check, because there is no such thing as
          releasing an empty delivery. Copy names a FILE CHECK, not a draft
          review: on a culling job there is nothing to watch, and the panel
          accepts nine extensions including .zip, so the failure this catches is
          the wrong project, the wrong NLE format, or an empty archive. */}
      {isAdmin && files.length > 0 && awaitingRelease(project) && (
        <div className="pt-1 space-y-2">
          <p className="font-sans text-[10px] text-slate-400 leading-relaxed">
            Nothing to watch here — open the file and confirm it before releasing:
            it is the right project, in the NLE the client ordered, and not an
            empty or truncated archive.
          </p>
          <button onClick={handleRelease} disabled={releasing} data-testid="culling-release-btn"
            className="w-full flex items-center justify-center gap-2 p-3 rounded-md bg-gold-leaf/[0.06] border border-gold-leaf/25 text-ug-gold font-sans text-[10px] font-semibold uppercase tracking-wider hover:bg-gold-leaf/10 transition-colors disabled:opacity-60">
            {releasing
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Releasing...</>
              : <><Check className="w-4 h-4" /> Release to Client</>}
          </button>
        </div>
      )}

      {checklist.needed && (
        <MustHavesPanel projectId={project.id} gate={checklist.gate} onChanged={checklist.load} onConfirm={() => setAttesting(true)} />
      )}
      {attesting && checklist.gate && (
        <AttestationModal projectId={project.id} items={checklist.gate.items} onClose={() => setAttesting(false)}
          onComplete={(att) => { checklist.confirmed(att); setAttesting(false); }} />
      )}
      {asksDelivery && <DeliveryQuestions value={delivery} onChange={setDelivery} />}
      <input ref={inputRef} type="file" multiple accept={CULLING_DELIVERABLE_EXTS.join(',')} className="hidden"
        onChange={(e) => e.target.files?.length && handleFiles(e.target.files)} data-testid="culling-file-input" />
      <button onClick={() => inputRef.current?.click()} disabled={uploading || checklist.needed || checklist.pending || (asksDelivery && !deliveryComplete(delivery))} data-testid="culling-upload-btn"
        className="w-full flex items-center justify-center gap-2 p-2.5 rounded-md border border-dashed border-ug-border text-[10px] font-mono uppercase tracking-wider text-slate-400 hover:text-ug-gold hover:border-ug-gold/30 transition-colors disabled:opacity-50">
        {uploading
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
          : <><Upload className="w-3.5 h-3.5" /> {files.length ? 'Deliver Again (new version)' : 'Deliver Culling File'}</>}
      </button>
      {asksDelivery && (checklist.needed || !deliveryComplete(delivery)) && (
        <p className="font-sans text-[9px] text-slate-500" data-testid="culling-delivery-hint">
          {checklist.needed ? 'Confirm the checklist above to deliver.' : 'Answer the two questions above to deliver.'}
        </p>
      )}
      <p className="font-sans text-[9px] text-slate-600">
        Accepted: {CULLING_DELIVERABLE_EXTS.join(' ')}
      </p>
    </div>
  );
};
